import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FORMAT_LABELS, errMsg } from "@/lib/api";

const blankQ = () => ({ question: "", options: ["", ""], answer_index: 0 });

function QuizBuilder({ quiz, onChange }) {
  const qs = quiz.questions;
  const setQ = (i, patch) => onChange({ ...quiz, questions: qs.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  return (
    <div className="space-y-4" data-testid="quiz-builder">
      <div className="space-y-1.5 sm:w-40"><Label>Pass mark (%)</Label><Input type="number" min={1} max={100} value={quiz.pass_mark} onChange={(e) => onChange({ ...quiz, pass_mark: Number(e.target.value) })} data-testid="quiz-pass-mark-input" /></div>
      {qs.map((q, i) => (
        <div key={i} className="space-y-2 rounded-xl border border-slate-200 p-3">
          <div className="flex gap-2">
            <Input value={q.question} onChange={(e) => setQ(i, { question: e.target.value })} placeholder={`Question ${i + 1}`} data-testid={`quiz-question-input-${i}`} />
            <Button type="button" variant="ghost" onClick={() => onChange({ ...quiz, questions: qs.filter((_, j) => j !== i) })} data-testid={`quiz-question-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
          {q.options.map((o, oi) => (
            <div key={oi} className="flex items-center gap-2 pl-2">
              <input type="radio" checked={q.answer_index === oi} onChange={() => setQ(i, { answer_index: oi })} aria-label="Correct answer" data-testid={`quiz-q${i}-correct-${oi}`} />
              <Input value={o} onChange={(e) => setQ(i, { options: q.options.map((x, k) => (k === oi ? e.target.value : x)) })} placeholder={`Option ${oi + 1}`} className="h-9" data-testid={`quiz-q${i}-option-input-${oi}`} />
              {q.options.length > 2 && <button type="button" onClick={() => setQ(i, { options: q.options.filter((_, k) => k !== oi), answer_index: 0 })} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
          {q.options.length < 6 && <Button type="button" size="sm" variant="ghost" onClick={() => setQ(i, { options: [...q.options, ""] })} data-testid={`quiz-q${i}-add-option`}>+ Option</Button>}
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange({ ...quiz, questions: [...qs, blankQ()] })} data-testid="quiz-add-question-btn"><Plus className="mr-1 h-4 w-4" /> Add question</Button>
      <p className="text-xs text-slate-500">Select the radio button next to the correct answer.</p>
    </div>
  );
}

export function LessonForm({ initial, onSubmit }) {
  const [f, setF] = useState({
    title: initial?.title || "", type: initial?.type || "text", content: initial?.content || "",
    video_url: initial?.video_url || "", resource_url: initial?.resource_url || "", resource_name: initial?.resource_name || "",
    live_url: initial?.live_url || "", live_at: initial?.live_at || "", duration_min: initial?.duration_min || 10,
    required: initial?.required ?? true, is_preview: initial?.is_preview ?? false,
    quiz: initial?.quiz || { pass_mark: 70, questions: [blankQ()] },
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const url = (v) => (v && v.trim()) || null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({ ...f, duration_min: Number(f.duration_min), video_url: url(f.video_url), resource_url: url(f.resource_url),
        live_url: url(f.live_url), quiz: f.type === "quiz" ? f.quiz : null });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const urlField = (k, label) => (
    <div className="space-y-1.5"><Label>{label}</Label><Input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder="https://" data-testid={`lesson-${k.replace("_", "-")}-input`} /></div>
  );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lesson-form">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5 sm:col-span-2"><Label>Lesson title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} required minLength={2} maxLength={140} data-testid="lesson-title-input" /></div>
        <div className="space-y-1.5">
          <Label>Type</Label>
          <Select value={f.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid="lesson-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(FORMAT_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`lesson-type-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {f.type === "video" && urlField("video_url", "Video link (YouTube or Vimeo)")}
      {f.type === "resource" && <div className="grid gap-4 sm:grid-cols-2">{urlField("resource_url", "File link")}<div className="space-y-1.5"><Label>File label</Label><Input value={f.resource_name} onChange={(e) => set("resource_name", e.target.value)} data-testid="lesson-resource-name-input" /></div></div>}
      {f.type === "live" && <div className="grid gap-4 sm:grid-cols-2">{urlField("live_url", "Meeting link")}<div className="space-y-1.5"><Label>When</Label><Input value={f.live_at} onChange={(e) => set("live_at", e.target.value)} placeholder="Fridays 4 PM WAT" data-testid="lesson-live-at-input" /></div></div>}
      {f.type === "quiz" ? <QuizBuilder quiz={f.quiz} onChange={(v) => set("quiz", v)} /> : (
        <div className="space-y-1.5">
          <Label>{f.type === "discussion" ? "Discussion prompt" : f.type === "project" ? "Project brief (objectives, deliverables, rubric)" : "Content"}</Label>
          <Textarea value={f.content} onChange={(e) => set("content", e.target.value)} rows={8} maxLength={20000} className="font-mono text-sm" data-testid="lesson-content-input" />
          <p className="text-xs text-slate-500">Use ## for headings, - for bullets, **bold** for emphasis.</p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-6">
        <div className="space-y-1.5 w-32"><Label>Minutes</Label><Input type="number" min={1} value={f.duration_min} onChange={(e) => set("duration_min", e.target.value)} data-testid="lesson-duration-input" /></div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.required} onCheckedChange={(v) => set("required", v)} data-testid="lesson-required-switch" /> Required for completion</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_preview} onCheckedChange={(v) => set("is_preview", v)} data-testid="lesson-preview-switch" /> Free preview</label>
      </div>
      <Button type="submit" disabled={busy} className="rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="lesson-form-submit">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save lesson
      </Button>
    </form>
  );
}
